import { useMutation, useQueryClient } from '@tanstack/vue-query'
import fetchApi from '@/utils/fetchApi'
import type { ConfigurationsListResult } from './useConfigurationsListQuery'

interface BulkDeleteResult {
  ok: boolean
  failedIds: string[]
}

export function useDeleteConfigurationsBulkMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (ids: string[]): Promise<BulkDeleteResult> => {
      if (ids.length === 0) return { ok: false, failedIds: [] }

      const uniqueIds = [...new Set(ids)]
      const res = await fetchApi<{
        deletedIds?: string[]
        failed?: { id: string; error?: string }[]
      }>({
        endpoint: '/api/configurations/bulk-delete',
        method: 'POST',
        body: { ids: uniqueIds },
      })

      const failedIds = (res.failed ?? []).map((f) => f.id)
      return failedIds.length > 0 ? { ok: false, failedIds } : { ok: true, failedIds: [] }
    },
    onSuccess: (result) => {
      if (result.ok) {
        // Optimistically zero the count so pagination updates immediately.
        // The backend search index can lag behind actual deletions, returning
        // stale total_hits while configs is already empty. The 1500ms refetch
        // in the view will correct the data once the index catches up.
        queryClient.setQueriesData<ConfigurationsListResult>(
          { queryKey: ['configurations', 'list'] },
          (old) => {
            if (!old) return old
            return {
              configs: [],
              pagination: { ...old.pagination, total_hits: 0, total_pages: 0, has_more: false },
            }
          },
        )
      }
      void queryClient.invalidateQueries({
        queryKey: ['configurations', 'list'],
        refetchType: 'none',
      })
      void queryClient.invalidateQueries({ queryKey: ['configurations', 'languages'] })
    },
  })
}
